import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Linkedin } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";
import ArticleRow from "@/components/ArticleRow";
import { CATEGORY_PAGES, categoryLabel } from "@/lib/categories";

export default function ArticlesIndex({ section, searchable = false }) {
  const { lang, tr } = useLang();
  const t = tr("common");
  const h = tr("home");
  const [articles, setArticles] = useState(null);
  const [cat, setCat] = useState("ALL");
  const [query, setQuery] = useState("");

  useSEO(section === "knowledge" ? "knowledge" : "insights");

  useEffect(() => {
    setArticles(null);
    api
      .get("/articles", { params: { section } })
      .then((r) => setArticles(r.data || []))
      .catch(() => setArticles([]));
  }, [section]);

  const items = articles || [];
  const cats = ["ALL", ...Array.from(new Set(items.map((a) => a.category)))];
  const q = query.trim().toLowerCase();
  const searched = q
    ? items.filter((a) => {
        const c = a[lang] || a.it || {};
        return [c.title, c.excerpt, c.body].some((v) => (v || "").toLowerCase().includes(q));
      })
    : items;
  const filtered = cat === "ALL" ? searched : searched.filter((a) => a.category === cat);
  const eyebrow = section === "knowledge" ? h.knowledgeEyebrow : tr("nav").insights;
  const titleTop = section === "knowledge" ? h.knowledgeTitle?.[0] : lang === "it" ? "Approfondimenti" : "In-depth analysis";
  const titleAccent = section === "knowledge" ? h.knowledgeTitle?.[1] : lang === "it" ? "sul commercio." : "on global trade.";

  return (
    <>
      <section className="relative overflow-hidden bg-navy px-6 pb-16 pt-36 noise lg:px-10 lg:pb-24 lg:pt-44">
        <div className="pointer-events-none absolute right-0 top-0 h-[420px] w-[420px] rounded-full bg-gold/10 blur-[130px]" />
        <div className="relative mx-auto max-w-7xl">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-gold">
              <span className="h-px w-10 bg-gold/70" /> {eyebrow}
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <h1 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-ivory sm:text-5xl lg:text-6xl" data-testid="articles-index-title">
              {titleTop} <em className="text-gold-bright">{titleAccent}</em>
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="min-h-[40vh] bg-navy-light px-6 py-14 lg:px-10 lg:py-20">
        <div className="mx-auto max-w-7xl">
          {searchable && (
            <div className="relative mb-6 max-w-md" data-testid="knowledge-search">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ivory/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                data-testid="knowledge-search-input"
                aria-label={t.searchPlaceholder}
                className="w-full border border-gold/25 bg-white/5 py-3 pl-11 pr-4 text-sm text-ivory placeholder:text-ivory/30 transition-colors focus:border-gold/60 focus:outline-none"
              />
            </div>
          )}
          <div className="mb-8 flex flex-wrap gap-2" data-testid="category-filter">
            {cats.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`border px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors ${
                  cat === c
                    ? "border-gold bg-gold text-navy font-semibold"
                    : "border-gold/25 text-ivory/60 hover:border-gold/60 hover:text-gold-bright"
                }`}
              >
                {c === "ALL" ? t.all : categoryLabel(c, lang)}
              </button>
            ))}
          </div>

          {articles === null ? (
            <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">{t.loading}</p>
          ) : filtered.length === 0 ? (
            <p className="border-t border-gold/15 py-16 text-center text-sm text-ivory/50" data-testid="articles-empty">
              {items.length === 0 ? t.noArticles : t.noResults}
            </p>
          ) : (
            <div data-testid="articles-list">
              {filtered.map((a, i) => (
                <Reveal key={a.id || a.slug} delay={Math.min(i * 0.06, 0.3)}>
                  <ArticleRow article={a} index={i} />
                </Reveal>
              ))}
            </div>
          )}

          {section === "knowledge" && (
            <Reveal delay={0.1}>
              <a
                href="https://www.linkedin.com/company/lb-trade-advisory"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="knowledge-linkedin-cta"
                className="group mt-14 flex flex-col items-start justify-between gap-6 border border-gold/20 bg-white/[0.02] p-8 transition-colors hover:border-gold/50 sm:flex-row sm:items-center"
              >
                <div className="flex items-start gap-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center bg-gold/15 text-gold-bright">
                    <Linkedin className="h-5 w-5" />
                  </span>
                  <span>
                    <span className="block font-serif text-xl text-ivory">{t.linkedinTitle}</span>
                    <span className="mt-1 block max-w-xl text-sm leading-relaxed text-ivory/50">{t.linkedinText}</span>
                  </span>
                </div>
                <span className="inline-flex shrink-0 items-center gap-2 border border-gold/40 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-bright transition-colors group-hover:bg-gold group-hover:text-navy">
                  {t.followPage}
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </a>
            </Reveal>
          )}

          <div className="mt-20" data-testid="category-pages">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">{t.exploreCategories}</p>
            <div className="mt-6 grid gap-4 md:grid-cols-3">
              {CATEGORY_PAGES.map((p) => (
                <Link
                  key={p.slug}
                  to={`/categoria/${p.slug}`}
                  data-testid={`category-link-${p.slug}`}
                  className="group flex h-full flex-col border border-gold/15 bg-white/[0.02] p-6 transition-colors hover:border-gold/50"
                >
                  <p className="font-serif text-xl text-ivory transition-colors group-hover:text-gold-bright">{p.label[lang]}</p>
                  <p className="mt-3 text-sm leading-relaxed text-ivory/50" style={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                    {p.intro[lang]}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-2 pt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-gold">
                    {t.viewArticles}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
